import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import Navbar from "../components/Navbar";
import NvdbRoadFactWidget from "../components/NvdbRoadFactWidget";
import WeakSubtopicsChip from "../components/WeakSubtopicsChip";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Timer, Target, Sparkles, BookOpen, ArrowRight, TrendingUp, Award, BarChart3, Activity } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stats").then(r => setStats(r.data)).finally(()=>setLoading(false));
  }, [lang]);

  const fmtPct = (v) => `${v ?? 0}%`;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar/>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-10">
          <p className="text-sm text-gray-500 mb-1">{t("welcome")}, {user?.name?.split(" ")[0]}</p>
          <h1 data-testid="dashboard-title" className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
            {t("dashboard")}
          </h1>
          <p className="text-gray-600 mt-1">{t("keepGoing")}</p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10 stagger">
          {[
            {icon:<Activity className="w-4 h-4"/>, label: t("statTotal"), val: stats?.total_attempts ?? 0, sub:""},
            {icon:<Award className="w-4 h-4"/>,    label: t("statMockBest"), val: fmtPct(stats?.mock_best), sub:""},
            {icon:<TrendingUp className="w-4 h-4"/>,label: t("statAccuracy"), val: fmtPct(stats?.avg_accuracy), sub:""},
            {icon:<BarChart3 className="w-4 h-4"/>, label: t("statPractice"), val: stats?.practice_attempts ?? 0, sub:""},
          ].map((s,i)=>(
            <Card key={i} data-testid={`stat-card-${i}`} className="p-5 bg-white border border-gray-200">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
                {s.icon}<span>{s.label}</span>
              </div>
              <div className="font-heading text-3xl font-bold text-gray-900">
                {loading ? "—" : s.val}
              </div>
            </Card>
          ))}
        </div>

        {/* Quick Start */}
        <h2 className="font-heading text-xl font-semibold mb-4">{t("quickStart")}</h2>
        <div className="grid md:grid-cols-3 gap-5 mb-12 stagger">
          <ActionCard data-testid="action-mock"
            icon={<Timer className="w-5 h-5"/>}
            title={t("fullMockExam")}
            description={t("fullMockExamDesc")}
            cta={t("startMock")}
            to="/mock"
            accent
          />
          <ActionCard data-testid="action-practice"
            icon={<Target className="w-5 h-5"/>}
            title={t("practiceByTopic")}
            description={t("practiceByTopicDesc")}
            cta={t("startPractice")}
            to="/practice"
          />
          <ActionCard data-testid="action-signs"
            icon={<Sparkles className="w-5 h-5"/>}
            title={t("signs")}
            description={t("signsSubtitle")}
            cta={t("signs")}
            to="/signs"
          />
        </div>

        {/* Recent attempts */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 bg-white border border-gray-200">
            <h3 className="font-heading text-lg font-semibold mb-4">{t("recentAttempts")}</h3>
            {(!stats?.recent || stats.recent.length === 0) ? (
              <div className="text-sm text-gray-500 py-8 text-center" data-testid="no-attempts">
                {t("noAttemptsYet")}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {stats.recent.map((a)=>(
                  <li key={a.attempt_id} data-testid={`recent-${a.attempt_id}`} className="py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-md grid place-items-center ${a.mode==="mock"?"bg-fjord text-white":"bg-fjord/10 text-fjord"}`}>
                        {a.mode === "mock" ? <Timer className="w-4 h-4"/> : <Target className="w-4 h-4"/>}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {a.mode === "mock" ? t("mockExam") : (a.topic || t("practice"))}
                        </div>
                        <div className="text-xs text-gray-500">{new Date(a.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant={a.passed ? "default" : "secondary"} className={a.passed?"bg-emerald-600":"bg-gray-200 text-gray-700"}>
                        {a.accuracy}%
                      </Badge>
                      <Link to={`/results/${a.attempt_id}`} className="text-fjord text-sm font-medium hover:underline">
                        {t("reviewAnswers")} →
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-6 bg-fjord text-white relative overflow-hidden">
            <div className="absolute -end-10 -top-10 w-40 h-40 rounded-full bg-white/10"></div>
            <BookOpen className="w-6 h-6 mb-3"/>
            <h3 className="font-heading text-lg font-semibold mb-2">{t("learnTitle")}</h3>
            <p className="text-sm text-white/80 mb-4">{t("learnSubtitle")}</p>
            <Link to="/learn">
              <Button data-testid="dashboard-learn-cta" variant="secondary" className="bg-white text-fjord hover:bg-gray-100">
                {t("learn")} <ArrowRight className="w-4 h-4 ms-1.5 rtl:rotate-180"/>
              </Button>
            </Link>
          </Card>
        </div>

        {/* Coached re-drill chip — appears when the user has a sub-topic <70% */}
        <WeakSubtopicsChip />

        {/* Live NVDB road fact + Real-Road Quiz */}
        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <NvdbRoadFactWidget />
          </div>
          <div className="lg:col-span-2">
            <Card className="p-6 bg-white border border-gray-200 h-full flex flex-col">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-md grid place-items-center bg-fjord/10 text-fjord shrink-0">
                  <Target className="w-5 h-5"/>
                </div>
                <div>
                  <Badge variant="outline" className="mb-1 text-[10px] uppercase tracking-wide">
                    {t("liveData")}
                  </Badge>
                  <h3 className="font-heading text-lg font-semibold text-gray-900">
                    {t("realRoadQuizTitle")}
                  </h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-5 leading-relaxed flex-1">
                {t("realRoadQuizSubtitle")}
              </p>
              <div>
                <Link to="/quiz?mode=nvdb&count=5">
                  <Button data-testid="dashboard-nvdb-quiz-cta" className="bg-fjord hover:bg-fjord-700">
                    {t("realRoadQuizCta")} <ArrowRight className="w-4 h-4 ms-1.5 rtl:rotate-180"/>
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

function ActionCard({icon, title, description, cta, to, accent, ...rest}) {
  return (
    <Card {...rest} className={`p-6 border ${accent?"border-fjord bg-fjord text-white":"border-gray-200 bg-white"} relative overflow-hidden group transition-shadow hover:shadow-lg`}>
      <div className={`w-10 h-10 rounded-md grid place-items-center mb-4 ${accent?"bg-white/15":"bg-fjord/10 text-fjord"}`}>
        {icon}
      </div>
      <h3 className="font-heading text-lg font-semibold mb-1">{title}</h3>
      <p className={`text-sm mb-5 ${accent?"text-white/85":"text-gray-600"}`}>{description}</p>
      <Link to={to}>
        <Button data-testid={`${rest["data-testid"]}-cta`} variant={accent?"secondary":"default"}
                className={accent?"bg-white text-fjord hover:bg-gray-100":"bg-fjord hover:bg-fjord-700"}>
          {cta} <ArrowRight className="w-4 h-4 ms-1.5 rtl:rotate-180"/>
        </Button>
      </Link>
    </Card>
  );
}
